import React, { createContext, useContext, useEffect, useState } from "react";

const LanguageContext = createContext({
    lang: "en",
    setLang: () => {},
    t: (key, fallback) => fallback,
});

const DICT = {
    en: {
        // nav
        "nav.cases": "Cases",
        "nav.skills": "Skills",
        "nav.about": "About",
        "nav.journal": "Journal",
        "nav.cta": "Get in touch",

        // hero
        "hero.eyebrow": "Available · UX Designer · 2026",
        "hero.title.a": "I design digital",
        "hero.title.b": "products that",
        "hero.title.c": "breathe",
        "hero.bio.role": "UX designer",
        "hero.bio.text":
            "shaped by empathy, curiosity and a stubborn streak of autonomy. I follow the plan when it serves the user, and quietly diverge from it when it doesn’t. Straight to the point, outside the box, goal driven.",
        "hero.cta.cases": "See my UX cases",
        "hero.cta.skills": "or peek at the skills →",
        "hero.card.hello": "Hello · 2026",
        "hero.card.fieldnote": "Field note 001",
        "hero.card.subtitle": "—— UX · Belgium",
        "hero.badge.open": "Charlyn De Wulf · UX/UI Designer ·",

        // philosophy
        "philo.eyebrow": "A little manifesto",
        "philo.line1.a": "I innovate ",
        "philo.line1.b": "when it’s needed",
        "philo.line2": "I follow the plan",
        "philo.line3": "and know when to diverge from it.",
        "philo.body":
            "UX, for me, is the patient art of listening. To research, to stakeholders, to the awkward silence between users and screens. And translating it into interfaces that simply work.",
        "philo.author": "Nils Conradi",
        "philo.author.role": "UX Designer & Strategist",

        // cases
        "cases.eyebrow": "Read the cases",
        "cases.title.a": "Read these ",
        "cases.title.b": "two cases",
        "cases.intro":
            "Each case opens a full write up with context, process, screens and learnings. Client names are kept confidential.",
        "cases.cardCta": "Read the full case",

        // skills
        "skills.eyebrow": "Hard skills · what I actually do",
        "skills.title.a": "The ",
        "skills.title.b": "toolkit",
        "skills.title.c": " behind the work.",
        "skills.intro":
            "Soft skills give the work its tone. Hard skills give it its shape. Below, the methods, tools and clients that have kept me curious.",
        "skills.workedWith": "Worked with · a shortlist",

        // little note
        "note.available": "Available for new projects",
        "note.chapter": "Folio · Chapter 02",
        "note.small": "A little note…",
        "note.title.a": "psst… want the full case?",
        "note.title.b": "let’s talk.",
        "note.cta": "Contact",

        // contact
        "contact.eyebrow": "Chapter 07 · Correspondence",
        "contact.title.a": "Let’s get",
        "contact.title.b": "in ",
        "contact.title.c": "touch",
        "contact.intro":
            "Currently open for new work. Get in touch to hear more about my projects.",
        "contact.label.name": "Your name",
        "contact.label.email": "Your email",
        "contact.label.story": "The story",
        "contact.placeholder.name": "Charlyn?",
        "contact.placeholder.email": "hello@world.com",
        "contact.placeholder.story": "What are we building together?",
        "contact.custom": "Custom requests",
        "contact.response": "Response within 48h",
        "contact.send": "Send the message",
        "contact.sending": "Sending…",

        // footer
        "footer.tag.a": "UX, slow and",
        "footer.tag.b": "deliberate.",
        "footer.tag.body":
            "Enterprise UX from start to finish, lean MVPs in tight loops, and the cross-functional glue in between.",
        "footer.navigate": "Navigate",
        "footer.copy": "Curated archive · © 2026",
        "footer.set": "Set in Cormorant & Manrope · printed on screen",
    },
    nl: {
        // nav
        "nav.cases": "Cases",
        "nav.skills": "Skills",
        "nav.about": "Over",
        "nav.journal": "Journal",
        "nav.cta": "Contact",

        // hero
        "hero.eyebrow": "Beschikbaar · UX Designer · 2026",
        "hero.title.a": "Ik ontwerp digitale",
        "hero.title.b": "producten die",
        "hero.title.c": "ademen",
        "hero.bio.role": "UX designer",
        "hero.bio.text":
            "gevormd door empathie, nieuwsgierigheid en een koppig stukje autonomie. Ik volg het plan als het de gebruiker dient, en wijk er stil van af als dat niet zo is. To the point, buiten de lijntjes, doelgericht.",
        "hero.cta.cases": "Bekijk mijn UX cases",
        "hero.cta.skills": "of kijk naar de skills →",
        "hero.card.hello": "Hallo · 2026",
        "hero.card.fieldnote": "Veldnota 001",
        "hero.card.subtitle": "—— UX · België",
        "hero.badge.open": "Charlyn De Wulf · UX/UI Designer ·",

        // philosophy
        "philo.eyebrow": "Een klein manifest",
        "philo.line1.a": "Ik innoveer ",
        "philo.line1.b": "als het nodig is",
        "philo.line2": "Ik volg het plan",
        "philo.line3": "en weet wanneer ik er van af mag wijken.",
        "philo.body":
            "UX is voor mij de geduldige kunst van het luisteren. Naar onderzoek, naar stakeholders, naar de stilte tussen gebruikers en schermen. En dat vertalen naar interfaces die gewoon werken.",
        "philo.author": "Nils Conradi",
        "philo.author.role": "UX Designer & Strategist",

        // cases
        "cases.eyebrow": "Lees de cases",
        "cases.title.a": "Lees deze ",
        "cases.title.b": "twee cases",
        "cases.intro":
            "Elke case is een volledig uitgewerkt verhaal met context, proces, schermen en learnings. Klantnamen blijven vertrouwelijk.",
        "cases.cardCta": "Lees de volledige case",

        // skills
        "skills.eyebrow": "Hard skills · wat ik écht doe",
        "skills.title.a": "De ",
        "skills.title.b": "toolkit",
        "skills.title.c": " achter het werk.",
        "skills.intro":
            "Soft skills geven het werk een toon. Hard skills geven het vorm. Hieronder de methodes, tools en klanten die mij nieuwsgierig houden.",
        "skills.workedWith": "Samengewerkt met · een shortlist",

        // little note
        "note.available": "Beschikbaar voor nieuwe projecten",
        "note.chapter": "Folio · Hoofdstuk 02",
        "note.small": "Een klein berichtje…",
        "note.title.a": "psst… de volledige case zien?",
        "note.title.b": "laten we praten.",
        "note.cta": "Contact",

        // contact
        "contact.eyebrow": "Hoofdstuk 07 · Correspondentie",
        "contact.title.a": "Laten we",
        "contact.title.b": "in ",
        "contact.title.c": "contact komen",
        "contact.intro":
            "Momenteel open voor nieuw werk. Neem contact op voor meer info over mijn projecten.",
        "contact.label.name": "Je naam",
        "contact.label.email": "Je e-mail",
        "contact.label.story": "Het verhaal",
        "contact.placeholder.name": "Charlyn?",
        "contact.placeholder.email": "hallo@wereld.com",
        "contact.placeholder.story": "Wat bouwen we samen?",
        "contact.custom": "Specifieke vragen",
        "contact.response": "Antwoord binnen 48u",
        "contact.send": "Verstuur het bericht",
        "contact.sending": "Versturen…",

        // footer
        "footer.tag.a": "UX, traag en",
        "footer.tag.b": "doordacht.",
        "footer.tag.body":
            "Enterprise UX van begin tot einde, lean MVPs in korte loops, en de cross-functionele lijm daar tussenin.",
        "footer.navigate": "Navigeer",
        "footer.copy": "Curated archive · © 2026",
        "footer.set": "Gezet in Cormorant & Manrope · gedrukt op scherm",
    },
};

export const LanguageProvider = ({ children }) => {
    const [lang, setLang] = useState(() => {
        if (typeof window === "undefined") return "en";
        return localStorage.getItem("cdw_lang") || "en";
    });

    useEffect(() => {
        localStorage.setItem("cdw_lang", lang);
        document.documentElement.setAttribute("lang", lang);
    }, [lang]);

    const t = (key, fallback = "") => {
        const dict = DICT[lang] || DICT.en;
        return dict[key] ?? DICT.en[key] ?? fallback;
    };

    return (
        <LanguageContext.Provider value={{ lang, setLang, t }}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => useContext(LanguageContext);
